/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getPatientProcedures } from 'app/entities/patient-procedure/patient-procedure.reducer';
import { getEntities as getIntraOPCharts } from 'app/entities/intra-op-chart/intra-op-chart.reducer';
import { OtherMonitors } from 'app/shared/model/enumerations/other-monitors.model';
import { createEntity, getEntity, reset, updateEntity } from './procedure-monitors.reducer';

export const ProcedureMonitorsUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const patientProcedures = useAppSelector(state => state.patientProcedure.entities);
  const intraOPCharts = useAppSelector(state => state.intraOPChart.entities);
  const procedureMonitorsEntity = useAppSelector(state => state.procedureMonitors.entity);
  const loading = useAppSelector(state => state.procedureMonitors.loading);
  const updating = useAppSelector(state => state.procedureMonitors.updating);
  const updateSuccess = useAppSelector(state => state.procedureMonitors.updateSuccess);
  const otherMonitorsValues = Object.keys(OtherMonitors);

  const handleClose = () => {
    navigate(`/procedure-monitors${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getPatientProcedures({}));
    dispatch(getIntraOPCharts({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.timeAdministered !== undefined && typeof values.timeAdministered !== 'number') {
      values.timeAdministered = Number(values.timeAdministered);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }

    const entity = {
      ...procedureMonitorsEntity,
      ...values,
      surgery: patientProcedures.find(it => it.id.toString() === values.surgery?.toString()),
      intraOPChart: intraOPCharts.find(it => it.id.toString() === values.intraOPChart?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          type: 'ETCO2',
          ...procedureMonitorsEntity,
          surgery: procedureMonitorsEntity?.surgery?.id,
          intraOPChart: procedureMonitorsEntity?.intraOPChart?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.procedureMonitors.home.createOrEditLabel" data-cy="ProcedureMonitorsCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.procedureMonitors.home.createOrEditLabel">Create or edit a ProcedureMonitors</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="procedure-monitors-id"
                  label={translate('tinyMedicRestApp.procedureMonitors.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureMonitors.type')}
                id="procedure-monitors-type"
                name="type"
                data-cy="type"
                type="select"
              >
                {otherMonitorsValues.map(otherMonitors => (
                  <option value={otherMonitors} key={otherMonitors}>
                    {translate(`tinyMedicRestApp.OtherMonitors.${otherMonitors}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureMonitors.timeAdministered')}
                id="procedure-monitors-timeAdministered"
                name="timeAdministered"
                data-cy="timeAdministered"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureMonitors.reading')}
                id="procedure-monitors-reading"
                name="reading"
                data-cy="reading"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureMonitors.notes')}
                id="procedure-monitors-notes"
                name="notes"
                data-cy="notes"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureMonitors.active')}
                id="procedure-monitors-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureMonitors.cursor')}
                id="procedure-monitors-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureMonitors.lastupdated')}
                id="procedure-monitors-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureMonitors.microSec')}
                id="procedure-monitors-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.procedureMonitors.timestampentry')}
                id="procedure-monitors-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="procedure-monitors-surgery"
                name="surgery"
                data-cy="surgery"
                label={translate('tinyMedicRestApp.procedureMonitors.surgery')}
                type="select"
                required
              >
                <option value="" key="0" />
                {patientProcedures
                  ? patientProcedures.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="procedure-monitors-intraOPChart"
                name="intraOPChart"
                data-cy="intraOPChart"
                label={translate('tinyMedicRestApp.procedureMonitors.intraOPChart')}
                type="select"
                required
              >
                <option value="" key="0" />
                {intraOPCharts
                  ? intraOPCharts.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/procedure-monitors" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ProcedureMonitorsUpdate;
